import React, { useState } from "react";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import {
  Container,
  TextField,
  Typography,
  Button,
  Box,
  Paper,
  MenuItem,
} from "@mui/material";
import { useForm, Controller } from "react-hook-form";
import * as yup from "yup";
import axios from "axios";
import ReCAPTCHA from "react-google-recaptcha";
import styles from "../styles/ContactForm.module.css";
import TimezoneSelect from "./TimezoneSelect";

const schema = yup.object().shape({
  first_name: yup.string().required("First Name is required"),
  last_name: yup.string().required("Last Name is required"),
  orgnization_email: yup
    .string()
    .email("Invalid email")
    .required("Email is required"),
  orgnization_name: yup.string().required("Org Name is required"),
  phone_number: yup
    .string()
    .matches(/^\d{10}$/, "Phone number must be 10 digits")
    .required("Phone number is required"),
  message: yup.string().required("Profession is required"),
  additional_information: yup.string().required("Please describe your idea"),
  how_did_you_hear: yup.string().required("how did you hear"),
  convenient_time: yup.string().required("Select a convenient time"),
});

const Contact = () => {
  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm({});

  const [captchaValue, setCaptchaValue] = useState(null);

  const onSubmit = async (data) => {
    if (!captchaValue) {
      alert("Please complete the CAPTCHA");
      return;
    }

    const payload = {
      ...data,
      form_type: "contact_us",
    };

    try {
      const response = await axios.post(
        "https://sfrv2dwdgc.execute-api.us-east-1.amazonaws.com/dev/akscel-demo-service/akscel_demo_model/create",
        payload
      );
      console.log("Form Submitted:", response.data);
      alert("Form submitted successfully!");
      reset();
    } catch (error) {
      console.error("Submission error:", error);
      alert("Failed to submit the form. Please try again.");
    }
  };

  return (
    <Box className={styles.Contactcontainer}>
      <Box className={styles.Contacttext}>
        <Typography variant="h4" fontWeight="bold" paddingTop={2}>
          Contact Us
        </Typography>
      </Box>

      {/* Form Section */}
      <Box className={styles.ContactForm}>
        <Container>
          <Typography variant="h5" fontWeight="bold">
            Get In Touch
          </Typography>
          <Typography sx={{ mt: 1, mb: 2 }} variant="body1">
            If you have any questions or need to schedule a demo, please fill
            out the form below. We do our best to respond within 1 business day.
          </Typography>

          <Typography fontWeight="bold" sx={{ mb: 1 }}>
            Mandatory fields are marked with{" "}
            <span style={{ color: "red" }}>*</span>
          </Typography>

          {/* Form Submission */}
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <form onSubmit={handleSubmit(onSubmit)}>
              {/* First Name */}
              <Typography paddingTop={5} variant="body2">
                First Name *
              </Typography>
              <Controller
                name="first_name"
                control={control}
                defaultValue=""
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    variant="standard"
                    placeholder="Enter"
                    error={!!errors.first_name}
                    helperText={errors.first_name?.message}
                    InputProps={{
                      style: {
                        fontSize: "12px",
                      },
                    }}
                  />
                )}
              />
              <Typography paddingTop={4} variant="body2">
                Last Name *
              </Typography>

              <Controller
                name="last_name"
                control={control}
                defaultValue=""
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    variant="standard"
                    placeholder="Enter"
                    error={!!errors.last_name}
                    helperText={errors.last_name?.message}
                    InputProps={{
                      style: {
                        fontSize: "12px",
                      },
                    }}
                  />
                )}
              />

              <Typography paddingTop={4} variant="body2">
                Email *
              </Typography>

              {/* Email */}
              <Controller
                name="orgnization_email"
                control={control}
                defaultValue=""
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    variant="standard"
                    placeholder="Enter"
                    error={!!errors.orgnization_email}
                    helperText={errors.orgnization_email?.message}
                    InputProps={{
                      style: {
                        fontSize: "12px",
                      },
                    }}
                  />
                )}
              />

              <Typography paddingTop={4} variant="body2">
                Phone Number *
              </Typography>

              {/* Phone Number */}
              <Controller
                name="phone_number"
                control={control}
                defaultValue=""
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    variant="standard"
                    placeholder="Enter"
                    error={!!errors.phone_number}
                    helperText={errors.phone_number?.message}
                    InputProps={{
                      style: {
                        fontSize: "12px",
                      },
                    }}
                  />
                )}
              />
              <Typography paddingTop={4} variant="body2">
                Name of Your Organization *
              </Typography>

              {/* Phone Number */}
              <Controller
                name="orgnization_name"
                control={control}
                defaultValue=""
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    variant="standard"
                    placeholder="Enter"
                    error={!!errors.orgnization_name}
                    helperText={errors.orgnization_name?.message}
                    InputProps={{
                      style: {
                        fontSize: "12px",
                      },
                    }}
                  />
                )}
              />

              <Typography paddingTop={4} variant="body2">
                Please specify your request *
              </Typography>

              <Controller
                name="message"
                control={control}
                defaultValue=""
                render={({ field }) => (
                  <TextField
                    {...field}
                    select
                    fullWidth
                    variant="standard"
                    placeholder="Select an option"
                    error={!!errors.message}
                    helperText={errors.message?.message}
                    InputProps={{
                      style: { fontSize: "12px" },
                    }}
                  >
                    <MenuItem value="akscel_demo">Akscel Demo</MenuItem>
                    <MenuItem value="services">Services</MenuItem>
                    <MenuItem value="careers">Careers</MenuItem>
                    <MenuItem value="careers">Others</MenuItem>
                  </TextField>
                )}
              />

              <Typography paddingTop={4} variant="body2">
                Provide Additional Information
              </Typography>

              {/* Idea */}
              <Controller
                name="additional_information"
                control={control}
                defaultValue=""
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    variant="standard"
                    placeholder="Enter"
                    error={!!errors.additional_information}
                    helperText={errors.additional_information?.message}
                    InputProps={{
                      style: {
                        fontSize: "12px",
                      },
                    }}
                  />
                )}
              />

              <Typography paddingTop={4} variant="body2">
                How did you hear about us?
              </Typography>

              <Controller
                name="how_did_you_hear"
                control={control}
                defaultValue="select"
                render={({ field }) => (
                  <TextField
                    {...field}
                    select
                    fullWidth
                    variant="standard"
                    placeholder="Select an option"
                    error={!!errors.how_did_you_hear}
                    helperText={errors.how_did_you_hear?.message}
                    InputProps={{
                      style: { fontSize: "12px" },
                    }}
                  >
                    <MenuItem value="GoogleSearch">Google Search</MenuItem>
                    <MenuItem value="Social_Media">
                      Social Media (LinkedIn, Instagram, X etc.)
                    </MenuItem>
                    <MenuItem value="Word_of_Mouth">Word of Mouth</MenuItem>
                    <MenuItem value="Advertisement">Advertisement</MenuItem>
                    <MenuItem value="Referral">
                      Referral from a Friend/Colleague
                    </MenuItem>
                  </TextField>
                )}
              />

              <Typography paddingTop={4} variant="body2">
                What would be the convenient time of day to contact you? *
              </Typography>

              {/* Time Selection */}

              <Controller
                name="convenient_time"
                control={control}
                rules={{ required: "Contact time is required" }}
                render={({ field }) => (
                  <DateTimePicker
                    {...field}
                    ampm={true}
                    format="DD/MM/YYYY hh:mm A"
                    minDateTime={dayjs()}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        variant: "standard",
                        margin: "normal",
                        error: !!errors.convenient_time,
                        helperText: errors.convenient_time?.message,
                        placeholder: "DD/MM/YYYY AM/PM",
                        InputProps: {
                          style: { fontSize: "12px" },
                        },
                        inputProps: {
                          placeholder: "DD/MM/YYYY AM/PM",
                        },
                      },
                    }}
                  />
                )}
              />

              <Typography paddingTop={2} variant="body2">
                Select your Timezone
              </Typography>

              <TimezoneSelect control={control} errors={errors} />

              <Box sx={{ mt: 3, mb: 3 }}>
                <ReCAPTCHA
                  sitekey="6LcXCt0pAAAAAD64OSyAnmuhMBG3Xa7IQPaWNo3N"
                  onChange={(value) => setCaptchaValue(value)}
                />
              </Box>

              {/* Submit Button */}
              <Button
                type="submit"
                variant="contained"
                disabled={isSubmitting}
                sx={{
                  mt: 2,
                  paddingLeft: 4,
                  paddingRight: 4,
                  paddingTop: 1,
                  paddingBottom: 1,
                  background: "linear-gradient(to right, #1995FC, #C416FF)",
                  color: "#fff",
                  "&:hover": { opacity: 0.9 },
                }}
              >
                {isSubmitting ? "Submitting..." : "Submit"}
              </Button>
            </form>
          </LocalizationProvider>
        </Container>
      </Box>
    </Box>
  );
};

export default Contact;
